import { Component, OnInit, inject } from '@angular/core';
import { HelperService } from './services/helper.service';
import { buyerInfo } from './constants/buyer_info';
import { TourStepTemplateComponent } from 'ngx-ui-tour-md-menu';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { HeaderComponent } from './blocks/header/header.component';
import { AsyncPipe, DatePipe } from '@angular/common';
import { LoadingService } from './services/loading.service';
import { setRedirecting, isRedirecting, getAppName, getUserDetailsRaw } from './shared/tenant.util';
import { AuthService } from './services/auth.service';
import { filter } from 'rxjs';
import { Banner, BannerService } from './services/banner.service';
import { environment } from '../environments/environment';

/**
 * Extracts valid tenant from URL path.
 * Returns null if no tenant or invalid tenant.
 */
function getValidTenantFromUrl(): string | null {
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  
  // Find tenant - it's the segment after 'obmui'
  const obmuiIndex = pathSegments.findIndex(s => s.toLowerCase() === 'obmui');
  if (obmuiIndex !== -1 && pathSegments.length > obmuiIndex + 1) {
    const tenant = pathSegments[obmuiIndex + 1];
    if (environment.tenants[tenant.toLowerCase()]) {
      return tenant.toLowerCase();
    }
  }
  
  // Fallback: check if first segment is a tenant
  if (pathSegments.length > 0 && environment.tenants[pathSegments[0].toLowerCase()]) {
    return pathSegments[0].toLowerCase();
  }
  
  return null;
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [RouterOutlet, TourStepTemplateComponent, AsyncPipe, HeaderComponent, DatePipe]
})
export class AppComponent implements OnInit {
  private helperService = inject(HelperService);
  private bannerService = inject(BannerService);
  private authService = inject(AuthService);
  private router = inject(Router);
  loadingService = inject(LoadingService);
  title = 'obm-angular-ui';
  userInfo: any;
  bannerList: Banner[] = [];
  isRedirecting = false;

  dismissBanner(uniqueID: string): void {
    this.bannerList = this.bannerList.filter(b => b.uniqueID !== uniqueID);
  }

  ngOnInit(): void {
    // CRITICAL: Check for tenant context IMMEDIATELY on app load.
    // Redirect to MAG portal ONLY when there is no tenant anywhere — neither a
    // valid tenant in the URL NOR a previously-stored appName. On a hard refresh
    // of an internal route (e.g. /obmui/supplier-home) the URL carries no tenant
    // segment because <base href> is a static "/obmui/", but the stored appName
    // still identifies the tenant — so we must NOT bounce those users to MAG.
    //
    // NOTE: We deliberately do NOT trust a persisted isRedirecting() flag here.
    // setRedirecting() writes to sessionStorage, which survives the full-page
    // round-trip to MAG and back within the tab. Reading it blindly on a fresh
    // load left the "Redirecting to login..." overlay stuck on pages such as
    // /terms-and-condition even when the tenant was fully resolvable (e.g. a
    // MAGBypass tenant) and no redirect was needed. Instead we recompute the
    // redirect decision below and clear the stale flag once we know we are
    // rendering the app rather than navigating away.
    const validTenant = getValidTenantFromUrl();
    const storedAppName = getAppName();

    if (!validTenant && !storedAppName) {
      const magLoginUrl = (environment as any).magLoginUrl;
      console.log('[AppComponent] No tenant context (URL or storage), redirecting to MAG portal:', magLoginUrl);

      if (magLoginUrl) {
        setRedirecting(true);
        this.isRedirecting = true;
        window.location.href = magLoginUrl;
        return;
      }
    }

    // Tenant context exists. Decide whether the auth guard is about to redirect this
    // WS-Fed tenant to MAG because there is no session yet. If so, keep the
    // "Redirecting to login..." overlay up so the app shell (header/empty body) never
    // flashes before that redirect. MAGBypass tenants and already-authenticated loads
    // render normally. When the guard finishes (authenticated → it clears the flag),
    // NavigationEnd re-syncs so the shell appears; if it redirects to MAG, the page
    // unloads first so the overlay simply stays until navigation.
    const appName = (validTenant || storedAppName || '').toLowerCase();
    const mode = (environment as any).tenantAuthMode?.[appName] ?? 'wsfed';
    const willRedirectToMag = mode === 'wsfed' && !this.authService.getToken();

    if (willRedirectToMag) {
      setRedirecting(true);
      this.isRedirecting = true;
      this.router.events
        .pipe(filter(e => e instanceof NavigationEnd))
        .subscribe(() => { this.isRedirecting = isRedirecting(); });
    } else {
      // Clear any stale redirect flag left over from a previous MAG round-trip.
      setRedirecting(false);
      this.isRedirecting = false;
    }

    console.log('[AppComponent] Tenant context found - URL:', validTenant, '| stored:', storedAppName);
    
    this.userInfo = getUserDetailsRaw();
    if(this.userInfo){
    this.helperService.getBuyerId().subscribe((res: any) => {
       buyerInfo.hostOrganizationUUID = res.hostOrganizationUUID;
       buyerInfo.userID = res.userID;
       buyerInfo.CampaignMicroserviceUrl = res.campaignMicroserviceUrl ? res.campaignMicroserviceUrl :'';
       buyerInfo.FormServicesUrl = res.formServicesUrl || res.FormServicesUrl || '';
       buyerInfo.FormsAPIToken = res.formsAPIToken || res.FormsAPIToken || '';
       buyerInfo.FormAPIAppID = res.formAPIAppID || res.FormAPIAppID || '';
     }
     );
    this.bannerService.getActiveBanners().subscribe((banners) => {
      this.bannerList = banners;
    });
  }
}
}
